import { z } from 'zod';

export const bookingSchema = z.object({
  service_id: z.string().min(1, 'Please select a service'),
  booking_date: z.string().min(1, 'Please select a date'),
  booking_time: z.string().min(1, 'Please select a time'),
  address: z.string().min(10, 'Address must be at least 10 characters long'),
  notes: z.string().optional(),
  is_recurring: z.boolean().optional(),
  recurring_frequency: z.enum(['weekly', 'biweekly', 'monthly']).optional(),
  recurring_end_date: z.string().optional(),
});

export const reviewSchema = z.object({
  rating: z.number().min(1).max(5),
  comment: z.string().min(10, 'Review must be at least 10 characters long'),
});

export const contactSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters long'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().regex(/^\d{10}$/, 'Please enter a valid 10-digit phone number'),
  message: z.string().min(10, 'Message must be at least 10 characters long'),
});

export const signupSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters long')
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    ),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

export const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

export const resetPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

export const updatePasswordSchema = z.object({
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters long')
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    ),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

export const addressSchema = z.object({
  label: z.string().min(1, 'Label is required'),
  address: z.string().min(10, 'Address must be at least 10 characters long'),
  is_default: z.boolean().optional(),
});

export const servicePreferencesSchema = z.object({
  preferred_day_of_week: z.array(z.number().min(1).max(7)),
  preferred_time_of_day: z.array(z.enum(['morning', 'afternoon', 'evening'])),
  special_instructions: z.string().optional(),
  preferred_duration: z.number().optional(),
});

export const communicationPreferencesSchema = z.object({
  email_notifications: z.boolean(),
  sms_notifications: z.boolean(),
  push_notifications: z.boolean(),
  preferred_language: z.string(),
  reminder_time_before: z.number().min(1).max(72),
  marketing_communications: z.boolean(),
  quiet_hours: z.object({
    start: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/),
    end: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/),
  }),
});

export type BookingFormData = z.infer<typeof bookingSchema>;
export type ReviewFormData = z.infer<typeof reviewSchema>;
export type ContactFormData = z.infer<typeof contactSchema>;
export type SignupFormData = z.infer<typeof signupSchema>;
export type LoginFormData = z.infer<typeof loginSchema>;
export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;
export type UpdatePasswordFormData = z.infer<typeof updatePasswordSchema>;
export type AddressFormData = z.infer<typeof addressSchema>;
export type ServicePreferencesFormData = z.infer<typeof servicePreferencesSchema>;
export type CommunicationPreferencesFormData = z.infer<typeof communicationPreferencesSchema>;